import { FC, useEffect, useState } from 'react';
import Loader from '../loader/Loader';
import Tooltip from '../tooltip/Tooltip';
import { SelectedSpecimenQuantity } from '../specimen-tables/SelectSpecimens';
import SpecimenLotInfo from '../specimen-lot-info/SpecimenLotInfo';
import { getSpecimensInventoryByCanisterId } from '../../api/specimensApi';
import { SpecimenCustomDataValue } from '../../types/interfaces';
import { EmbryoStraw, Specimen } from '../../types/interfaces/specimen.interfaces';
import { showToast } from '../../services/toast.service';
import { toastMessages } from '../../constants/errorMessages';
import { LABEL_CONSTANTS, TABLE_HEADER_CONSTANTS } from '../../constants/common';
import './selectSpecimenFromCanisterTable.scss';

type SelectSpecimenFromCanisterTableProps = {
  canisterId?: number;
  onChange: (e: { valid: boolean; value: SelectedSpecimenQuantity[] }) => void;
  selectedSpecimenId?: number;
};

const SelectSpecimenFromCanisterTable: FC<SelectSpecimenFromCanisterTableProps> = ({
  canisterId,
  onChange,
  selectedSpecimenId,
}) => {
  const [specimens, setSpecimens] = useState<Specimen[]>([]);
  const [quantities, setQuantities] = useState<SelectedSpecimenQuantity[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isEmbryo, setIsEmbryo] = useState<boolean>(false);

  useEffect(() => {
    if (!canisterId) {
      setSpecimens([]);
      return;
    }

    const fetchInventory = async () => {
      try {
        setLoading(true);
        const res = await getSpecimensInventoryByCanisterId(canisterId);
        const data = res.data || [];

        const specimenType = data.some((spec: Specimen) => spec.specimenType?.name === 'Embryo');
        setIsEmbryo(specimenType);

        const specimenRows: Specimen[] = [];
        const quants: SelectedSpecimenQuantity[] = [];

        for (const spec of data) {
          const flushMating =
            spec.specimenCustomDataValues?.find(
              (item: SpecimenCustomDataValue) => item.specimenCustomDataKey?.keyName === 'Flush Mating',
            )?.value ?? '-';

          if (Array.isArray(spec.embryoStraws) && spec.embryoStraws.length > 0) {
            spec.embryoStraws.forEach((straw: EmbryoStraw) => {
              specimenRows.push({
                ...spec,
                embryoStrawCode: straw.embryoStrawCode,
                embryoStrawId: straw.embryoStrawId,
                embryoGrade: straw.embryoGrade,
                embryoStage: straw.embryoStage,
                flushMating,
              });

              quants.push({
                specimenId: spec.specimenId,
                availableQuantity: straw.embryoCountPerStraw ?? straw.embryoCountPerStraw ?? 0,
                selectedCanisterId: spec.specimenLocations?.[0]?.storageCanisterId,
                quantity: 0,
                selected: false,
                embryoStrawId: straw.embryoStrawId,
                embryoStrawCode: straw.embryoStrawCode,
                embryoStrawNumber: straw.embryoStrawNumber,
                flushMating,
              });
            });
          } else {
            specimenRows.push(spec);
            quants.push({
              specimenId: spec.specimenId,
              availableQuantity: spec.quantityAvailable ?? 0,
              selectedCanisterId: spec.specimenLocations?.[0]?.storageCanisterId,
              quantity: 0,
              selected: false,
            });
          }
        }

        setSpecimens(specimenRows);
        setQuantities(quants);
      } catch (error) {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      } finally {
        setLoading(false);
      }
    };

    fetchInventory();
  }, [canisterId]);

  useEffect(() => {
    const quants = quantities.filter(q => q.selected);
    const valid = quants.reduce((acc: boolean, q: SelectedSpecimenQuantity): boolean => {
      return acc && q.quantity > 0 && q.quantity <= q.availableQuantity;
    }, quants.length > 0);

    onChange({ valid: valid, value: quants });
    quants.every((q: SelectedSpecimenQuantity) => {
      if (q.quantity > q.availableQuantity) {
        showToast.error(toastMessages.CHOSE_MORE_THAN_TOTAL);
      }
    });
  }, [quantities]);

  const inputCheckbox = (index: number, specimenType?: string) => {
    const isEmbryoType = specimenType === 'Embryo';
    return (
      <input
        type="checkbox"
        checked={quantities[index]?.selected ?? false}
        onChange={e => {
          const checked = e.target.checked;
          const newQuantities = [...quantities];
          newQuantities[index] = {
            ...quantities[index],
            selected: checked,
            quantity: checked ? (isEmbryoType ? quantities[index].availableQuantity : quantities[index].quantity) : 0,
          };
          setQuantities(newQuantities);
        }}
      />
    );
  };

  const inputQuantityElement = (index: number, specimenType?: string) => {
    const isEmbryoType = specimenType === 'Embryo';
    const selectedQuantity = quantities[index];

    return (
      <input
        type="text"
        placeholder="0"
        value={
          selectedQuantity.selected
            ? isEmbryoType
              ? selectedQuantity.availableQuantity
              : selectedQuantity.quantity > 0
              ? selectedQuantity.quantity
              : ''
            : '-'
        }
        disabled={!selectedQuantity.selected || isEmbryoType}
        onChange={e => {
          if (isEmbryoType) return;
          const count = +e.target.value.replace(/[^\d]+/, '').replace(/^0+/, '');
          const newQuantities = [...quantities];
          newQuantities[index] = { ...selectedQuantity, quantity: count };
          setQuantities(newQuantities);
        }}
      />
    );
  };

  return (
    <>
      <div className="desk-move-select-specimen">
        <div className="specimen-table">
          <table>
            <thead>
              <tr>
                <th>{TABLE_HEADER_CONSTANTS.SELECT}</th>
                <th>{TABLE_HEADER_CONSTANTS.ANIMAL}</th>
                <th>{TABLE_HEADER_CONSTANTS.INVENTORY_TYPE}</th>
                <th>{TABLE_HEADER_CONSTANTS.LOT_DATE_NO}</th>
                <th>{TABLE_HEADER_CONSTANTS.STRAW_LOT}</th>
                <th>{TABLE_HEADER_CONSTANTS.STRAW_NUMBER}</th>
                <th>{TABLE_HEADER_CONSTANTS.QUALITY}</th>
                <th>{TABLE_HEADER_CONSTANTS.QTY_IN_CANISTER}</th>
                <th>{TABLE_HEADER_CONSTANTS.QUANTITY}</th>
              </tr>
            </thead>
            <tbody>
              {specimens?.map((spec, index) => {
                return (
                  <tr
                    className={
                      quantities[index]?.selected
                        ? quantities[index]?.quantity > quantities[index]?.availableQuantity
                          ? 'error'
                          : quantities[index]?.quantity === 0
                          ? 'warning'
                          : ''
                        : ''
                    }
                    key={'specimen-row:' + spec.specimenId + '-index:' + index}>
                    <td>{inputCheckbox(index, spec.specimenType?.name)}</td>
                    <td>
                      {spec.animal?.code}-{spec.animal?.name}
                    </td>
                    <td>{spec.specimenType?.name}</td>
                    <td>
                      <SpecimenLotInfo specimenId={spec.specimenId} lotNumber={spec?.lotNumber} freezeDate={spec.freezeDate} />
                    </td>
                    <td key={spec.embryoStrawId}>
                      {spec.embryoStrawCode ?? '-'}
                      {spec.embryoStrawCode && (
                        <Tooltip>
                          {`${LABEL_CONSTANTS.EMBRYO_GRADE}: ${spec.embryoGrade || 'NA'}`} <br />
                          {`${LABEL_CONSTANTS.EMBRYO_STAGE}: ${spec.embryoStage || 'NA'}`}
                        </Tooltip>
                      )}
                    </td>
                    <td>{spec.embryoStrawNumber ?? '-'}</td>
                    <td>{spec.quality}</td>
                    <td>{quantities[index]?.availableQuantity}</td>
                    <td>{inputQuantityElement(index, spec.specimenType?.name)}</td>
                  </tr>
                );
              })}
              <tr>
                <td colSpan={8}>{LABEL_CONSTANTS.TOTAL_QUANTITY}:</td>
                <td>
                  <input
                    type="text"
                    disabled
                    value={quantities.reduce(
                      (acc: number, qua: SelectedSpecimenQuantity): number => (qua.selected ? acc + qua.quantity : acc),
                      0,
                    )}
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile View */}
      <div className="xs-move-select-specimen">
        {loading ? (
          <Loader addedSpace simple loaderSize={'small'} />
        ) : specimens?.length === 0 && !loading ? (
          <label className="no-inventory-found">{LABEL_CONSTANTS.NO_INVENTORY_FOUND}</label>
        ) : (
          <>
            {specimens?.map((spec, index) => {
              return (
                <div className="select-specimen-container" key={'specimen-row:' + spec.specimenId + '-index:' + index}>
                  <div className="card max-width">
                    {quantities[index]?.selected ? (
                      <div className="selected-specimen-row">
                        <div className="input-check">{inputCheckbox(index, spec.specimenType?.name)}</div>
                        <div className="row-content">
                          <div className="header">
                            <label>
                              {spec.animal?.code}-{spec.animal?.name}
                            </label>
                            <label>
                              <SpecimenLotInfo
                                specimenId={spec.specimenId}
                                lotNumber={spec?.lotNumber}
                                freezeDate={spec.freezeDate}
                              />
                            </label>
                          </div>
                          <hr />
                          <div className="body">
                            <div className="spec-info">
                              <label>{`${LABEL_CONSTANTS.LOT_NUMBER_TYPE}: ${spec.specimenId} -${spec.specimenType?.name} `}</label>
                              <label>{`${TABLE_HEADER_CONSTANTS.QUALITY}: ${spec.quality}`}</label>
                              <label>{`${TABLE_HEADER_CONSTANTS.QTY_IN_CANISTER}: ${quantities[index]?.availableQuantity}`}</label>
                            </div>
                            <div className="input-body">
                              <label>{LABEL_CONSTANTS.SELECTED_QTY}</label>
                              <input
                                type="text"
                                placeholder="0"
                                value={
                                  quantities[index]?.selected
                                    ? quantities[index]?.quantity > 0
                                      ? quantities[index]?.quantity
                                      : ''
                                    : '-'
                                }
                                disabled={!quantities[index]?.selected}
                                onChange={e => {
                                  const count = +e.target.value.replace(/[^\d]+/, '').replace(/^0+/, '');
                                  let newQuantities = [...quantities];
                                  newQuantities[index] = {
                                    ...quantities[index],
                                    quantity: count,
                                  };
                                  setQuantities(newQuantities);
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="select-specimen-row">
                        <div className="body">
                          <div className="input-body">
                            {inputCheckbox(index, spec.specimenType?.name)}
                            <label>
                              {spec.animal?.code}-{spec.animal?.name}
                            </label>
                          </div>
                          <label>
                            <SpecimenLotInfo
                              specimenId={spec.specimenId}
                              lotNumber={spec?.lotNumber}
                              freezeDate={spec.freezeDate}
                            />
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            <div className="total-quantity">
              <label>{LABEL_CONSTANTS.TOTAL_SELECTED}: </label>
              {quantities.reduce(
                (acc: number, qua: SelectedSpecimenQuantity): number => (qua.selected ? acc + qua.quantity : acc),
                0,
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default SelectSpecimenFromCanisterTable;
